import { useMemo } from 'react';
import useServerStateContext from './useServerStateContext';

/**
 * Hook that returns the selected camera with display name from game camera configuration
 * This synchronizes the serverState camera selection with game camera display names
 */
export const useSelectedCameraWithDisplayName = () => {
  const { selectedCamera } = useServerStateContext();

  const selectedCameraWithDisplayName = useMemo(() => {
    if (!selectedCamera) return null;

    // The selectedCamera from serverState already contains the game camera data
    // It has displayName, camIndex, etc. - this is actually a GameCamera object!
    const displayName = (selectedCamera as any).displayName || selectedCamera.name || `Camera ${(selectedCamera as any).camIndex || 'Unknown'}`;
    
    return {
      ...selectedCamera,
      displayName
    };
  }, [selectedCamera]);

  return selectedCameraWithDisplayName;
};

/**
 * Hook that provides a function to get camera display name by camera index
 * Useful for camera lists and forms where you need display names for any camera
 */
export const useCameraDisplayNames = () => {
  const { availableCameras } = useServerStateContext();

  const getCameraDisplayName = useMemo(() => {
    return (cameraIdx: number) => {
      // Handle undefined/null cameraIdx
      if (cameraIdx === undefined || cameraIdx === null) {
        return 'Unknown Camera';
      }

      if (!Array.isArray(availableCameras)) {
        return `Camera ${cameraIdx}`;
      }

      const camera = availableCameras.find(cam => {
        const camIndexValue = (cam as any).camIndex ?? cam.idx;
        return camIndexValue === cameraIdx;
      });
      
      // The camera object from availableCameras should have displayName if it's a GameCamera
      const displayName = (camera as any)?.displayName || camera?.name || `Camera ${cameraIdx}`;
      return displayName;
    };
  }, [availableCameras]);

  return { getCameraDisplayName };
};

export default useSelectedCameraWithDisplayName;